import type { CeoPaymentApprovalRow } from '@shared/api';
import { moneyInput, parseMoney } from '@shared/lib/money';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { formatDateRu, formatMoney, PAYMENT_CATEGORY_LABELS } from '../ceoFormat';
import {
  AmountColumn,
  ApprovedAmountText,
  BankDocText,
  CarryoverBadge,
  CarryoverBadgeText,
  CategoryText,
  CounterpartyText,
  DecisionActionsRow,
  DemoBadge,
  ExecutedText,
  InlineAmountError,
  InlineInputRowEnd,
  InitiatorText,
  MiniBtn,
  MutedDateText,
  PaidText,
  PurposeTd,
  RemainingBalanceText,
  RequestNumberRow,
  RequestNumberText,
  RequestedText,
  RightAlignTd,
  SmallInput,
  StickyRecipientTd,
  StickyTd,
} from './styles';
import type { DecideApprovalFn } from './types';

interface ApprovalRowProps {
  item: CeoPaymentApprovalRow;
  /** Текст поля суммы к акцепту; `undefined` — Гендиректор его не правил. */
  inlineAmount: string | undefined;
  onInlineAmountChange: (id: string, text: string) => void;
  can_write: boolean;
  isDeciding: boolean;
  onDecide: DecideApprovalFn;
}

export function ApprovalRow({
  item,
  inlineAmount,
  onInlineAmountChange,
  can_write,
  isDeciding,
  onDecide,
}: ApprovalRowProps) {
  const requestedAmt = item.requested_amount || item.amount;
  const serverAmt = Math.min(
    requestedAmt,
    item.approved_amount && item.approved_amount > 0 ? item.approved_amount : item.amount
  );
  const amountText = inlineAmount ?? moneyInput(serverAmt);
  const parsedAmt = parseMoney(amountText);
  const amountProblem =
    parsedAmt === null
      ? 'Сумма числом, например 125 000,50'
      : parsedAmt > requestedAmt + 0.005
        ? `Не больше запрошенного: ${formatMoney(requestedAmt)}`
        : null;
  // Сумма к акцепту: `null`, пока в поле не сумма, — тогда и акцептовать нельзя.
  const currentVal = amountProblem === null ? parsedAmt : null;
  const cutAmt = currentVal === null ? 0 : Math.max(0, requestedAmt - currentVal);
  const setShare = (share: number) =>
    onInlineAmountChange(item.id, moneyInput(Math.round(requestedAmt * share * 100) / 100));
  const isExecutedOrPaid =
    item.decision === 'executed' || item.payment_execution_status === 'paid';

  return (
    <tr>
      <td>
        <RequestNumberRow>
          <RequestNumberText>{item.request_number}</RequestNumberText>
          {(item.id.startsWith('a1000001-') || item.id.startsWith('f1000001-')) && (
            <DemoBadge $tone="neutral">Демо-данные</DemoBadge>
          )}
        </RequestNumberRow>
        {item.request_number.includes('-ОСТ') && (
          <CarryoverBadgeText>
            ↩ Перенесённый остаток из реестра
          </CarryoverBadgeText>
        )}
        <MutedDateText>{formatDateRu(item.planned_pay_date)}</MutedDateText>
      </td>
      <td>
        <Badge
          $tone={
            item.priority === 'critical'
              ? 'danger'
              : item.priority === 'high'
                ? 'warning'
                : 'neutral'
          }
        >
          {item.priority === 'critical'
            ? 'Критичный'
            : item.priority === 'high'
              ? 'Высокий'
              : item.priority === 'normal'
                ? 'Плановый'
                : 'Низкий'}
        </Badge>
      </td>
      <StickyRecipientTd>
        <CounterpartyText>{item.counterparty_name}</CounterpartyText>
        <CategoryText>
          {item.bank_name ?? 'Основной р/с'} ·{' '}
          {PAYMENT_CATEGORY_LABELS[item.category] ?? item.category}
        </CategoryText>
      </StickyRecipientTd>
      <PurposeTd>
        <div>{item.purpose}</div>
        <InitiatorText>
          Инициатор: {item.initiator_name ?? '—'}
          {item.decision_comment ? ` · ${item.decision_comment}` : ''}
        </InitiatorText>
      </PurposeTd>
      <td>
        <Badge
          $tone={
            item.budget_status === 'within_limit'
              ? 'success'
              : item.budget_status === 'over_limit'
                ? 'warning'
                : 'danger'
          }
        >
          {item.budget_status === 'within_limit'
            ? 'В лимите БДДС'
            : item.budget_status === 'over_limit'
              ? 'Сверх лимита'
              : 'Вне бюджета'}
        </Badge>
      </td>

      {/* Запрошено / К акцепту */}
      <RightAlignTd>
        <AmountColumn>
          <RequestedText>
            Запрошено: <b>{formatMoney(requestedAmt)}</b>
          </RequestedText>

          {can_write &&
          item.decision !== 'approved' &&
          item.decision !== 'executed' &&
          !isExecutedOrPaid ? (
            <>
              <InlineInputRowEnd>
                <SmallInput
                  type="text"
                  inputMode="decimal"
                  value={amountText}
                  aria-label={`Сумма к акцепту по заявке ${item.request_number}`}
                  aria-invalid={amountProblem ? true : undefined}
                  onChange={(e) => onInlineAmountChange(item.id, e.target.value)}
                />
                <MiniBtn type="button" onClick={() => setShare(1)}>
                  100%
                </MiniBtn>
                <MiniBtn type="button" onClick={() => setShare(0.5)}>
                  50%
                </MiniBtn>
                <MiniBtn type="button" onClick={() => setShare(0.3)}>
                  30%
                </MiniBtn>
              </InlineInputRowEnd>
              {amountProblem && <InlineAmountError>{amountProblem}</InlineAmountError>}

              {cutAmt > 0 && (
                <CarryoverBadge>
                  ↪ Срез {formatMoney(cutAmt)} переносится в след. реестр
                </CarryoverBadge>
              )}
            </>
          ) : (
            <ApprovedAmountText>
              {formatMoney(item.approved_amount || item.amount)}
              {item.cut_amount && item.cut_amount > 0 ? (
                <CarryoverBadge>
                  ↪ Срезано в след. реестр: {formatMoney(item.cut_amount)}
                </CarryoverBadge>
              ) : null}
            </ApprovedAmountText>
          )}
        </AmountColumn>
      </RightAlignTd>

      {/* Факт по выписке банка */}
      <RightAlignTd>
        <AmountColumn>
          <Badge
            $tone={
              item.payment_execution_status === 'paid'
                ? 'success'
                : item.payment_execution_status === 'partially_paid'
                  ? 'warning'
                  : 'neutral'
            }
          >
            {item.payment_execution_status === 'paid'
              ? 'Оплачено'
              : item.payment_execution_status === 'partially_paid'
                ? 'Частично'
                : 'Ожидает выписки'}
          </Badge>
          <PaidText>
            Оплачено: <b>{formatMoney(item.paid_amount || 0)}</b>
          </PaidText>
          {item.payment_execution_status !== 'paid' && (
            <RemainingBalanceText>
              Остаток:{' '}
              <b>
                {formatMoney(
                  Math.max(
                    0,
                    (item.approved_amount || item.amount) - (item.paid_amount || 0)
                  )
                )}
              </b>
            </RemainingBalanceText>
          )}
          {item.bank_doc_numbers && <BankDocText>{item.bank_doc_numbers}</BankDocText>}
        </AmountColumn>
      </RightAlignTd>

      <td>
        <Badge
          $tone={
            item.decision === 'approved' || item.decision === 'executed'
              ? 'success'
              : item.decision === 'rejected'
                ? 'danger'
                : item.decision === 'deferred'
                  ? 'neutral'
                  : 'warning'
          }
        >
          {item.decision === 'approved'
            ? 'Акцептовано'
            : item.decision === 'executed'
              ? 'Исполнено'
              : item.decision === 'deferred'
                ? 'Отложено'
                : item.decision === 'rejected'
                  ? 'Отклонено'
                  : 'Ожидает решения'}
        </Badge>
      </td>

      {can_write && (
        <StickyTd>
          {isExecutedOrPaid ? (
            <ExecutedText>
              {item.decision === 'executed' ? 'Исполнено банком' : 'Оплачено по выписке'}
            </ExecutedText>
          ) : (
            <DecisionActionsRow>
              {item.decision !== 'approved' && (
                <Button
                  $size="sm"
                  disabled={isDeciding || currentVal === null}
                  title={amountProblem ?? undefined}
                  onClick={() => {
                    if (currentVal === null) return;
                    const sanitizedApproved = Math.min(requestedAmt, currentVal);
                    const sanitizedCut = Math.max(0, requestedAmt - sanitizedApproved);
                    const comment =
                      sanitizedCut > 0
                        ? `Акцептовано Генеральным директором на ${formatMoney(sanitizedApproved)}, срез ${formatMoney(sanitizedCut)} перенесён в след. реестр`
                        : 'Акцептовано Генеральным директором к оплате';
                    onDecide(item.id, 'approved', sanitizedApproved, comment);
                  }}
                >
                  Акцептовать
                </Button>
              )}
              {item.decision !== 'deferred' && (
                <Button
                  $size="sm"
                  $variant="secondary"
                  disabled={isDeciding}
                  onClick={() =>
                    onDecide(
                      item.id,
                      'deferred',
                      undefined,
                      'Отложено до следующего платёжного дня'
                    )
                  }
                >
                  Отложить
                </Button>
              )}
              {item.decision !== 'rejected' && (
                <Button
                  $size="sm"
                  $variant="ghost"
                  disabled={isDeciding}
                  onClick={() =>
                    onDecide(
                      item.id,
                      'rejected',
                      undefined,
                      'Отклонено Генеральным директором'
                    )
                  }
                >
                  Отклонить
                </Button>
              )}
            </DecisionActionsRow>
          )}
        </StickyTd>
      )}
    </tr>
  );
}
